import React, { FC, Suspense, useCallback, useMemo, useState } from 'react';

import formatNumberWithCurrency from '@eika-infrastruktur/format/dist/formatNumberWithCurrency';
import { useQueryClient } from '@eika-infrastruktur/react-query';
import { Button } from '@eika/button';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import EFakturaEmptyList from '../../components/EFakturaList/EFakturaEmptyList';
import { EFakturaItem } from '../../components/EFakturaList/EFakturaItem';
import { EikaModal } from '../../components/EikaModal';
import { ErrorCatcher } from '../../components/ErrorCatcher';
import EFakturaModal from '../../components/transaction/EFaktura/EFakturaModal';
import { ACCOUNTS_QUERY_KEY, paymentFromSelector, useSuspendedAccounts } from '../../fetchHooks/useAccount';
import { EFAKTURA_QUERY_KEY, useSuspendedEFakturas } from '../../fetchHooks/useEfaktura';
import { EFaktura } from '../../generated-types';
import useTranslation, { format } from '../../hooks/useTranslation';
import { AnalyticsEvents } from '../../services/analytics';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import { eFakturaTrackingIDs } from '../../utils/gtm/trackIDs/eFaktura';
import MultipleApprovalEFAkturaListIntro from './MultipleApprovalEFAkturaListIntro';
import MultipleApprovalEFakturaListSkeleton from './MultipleApprovalEFakturaListSkeleton';
import MultipleApprovalEFakturaModalContent from './MultipleApprovalEFakturaModalContent';
import useSelectedDebitAccount from './hooks/useSelectedDebitAccount';
import { getCombinedEFakturaAmount } from './util/getCombinedEFakturaAmount';

import './MultipleApprovalEFakturaList.scss';

const MultipleApprovalEFakturaList: FC = () => {
    const translated = useTranslation();

    const [openEFaktura, setOpenEFaktura] = useState(false);
    const [targetEFaktura, setTargetEFaktura] = useState<EFaktura>();

    const { data: eFakturas, isRefetching } = useSuspendedEFakturas();

    const queryClient = useQueryClient();

    const { data: accounts } = useSuspendedAccounts({ select: paymentFromSelector });

    const [error, setError] = useState<string>();
    const debitAccountState = useSelectedDebitAccount(accounts, setError);
    const [, , debitAccount, resetDebitAccount] = debitAccountState;

    const { dispatch, handleCancelSigningFlow, dynamicLinkId, signingError } = useSigningContext();

    const formattedTotalAmount = useMemo(() => {
        if (eFakturas.length === 0) return '';
        const combinedAmount = getCombinedEFakturaAmount(eFakturas);
        return formatNumberWithCurrency(combinedAmount, String(eFakturas[0].currency), 2);
    }, [eFakturas]);

    const [openApproveAllModal, setOpenApproveAllModal] = useState(false);

    const refetchAndReset = useCallback(async () => {
        if (signingError || dynamicLinkId) await queryClient.invalidateQueries({ queryKey: EFAKTURA_QUERY_KEY });
        if (dynamicLinkId) {
            handleCancelSigningFlow();
        }
        setError(undefined);
        resetDebitAccount();
        setTargetEFaktura(undefined);
        dispatch({ type: Actions.RESET });
    }, [signingError, dynamicLinkId, queryClient, resetDebitAccount, dispatch, handleCancelSigningFlow]);

    if (eFakturas.length === 0) {
        return (
            <div className="sgw-content">
                <EFakturaEmptyList />
            </div>
        );
    }

    return (
        <section className="multi-approval-efaktura sgw-content">
            <MultipleApprovalEFAkturaListIntro
                isRefetching={isRefetching}
                numberOfEFakturas={eFakturas.length}
                formattedTotalAmount={formattedTotalAmount}
            />

            <ul className="multi-approval-efaktura__list">
                {eFakturas.map((eFaktura) => (
                    <EFakturaItem
                        isLoading={isRefetching}
                        showPaymentTypeLabel={false}
                        key={eFaktura.id.value}
                        eFaktura={eFaktura}
                        openModal={() => {
                            setOpenEFaktura(true);
                            setTargetEFaktura(eFaktura);
                        }}
                    />
                ))}
            </ul>
            {eFakturas.length > 0 && (
                <Button
                    className="multi-approval-efaktura__approve-all-button"
                    data-track-id={eFakturaTrackingIDs.batchEFaktura.id}
                    data-track-linkname={translated.approveMultipleEFaktura.approveEFaktura}
                    onClick={() => setOpenApproveAllModal(true)}
                >
                    {format(translated.approveMultipleEFaktura.approveAndPayNEFaktura, eFakturas.length)}
                </Button>
            )}

            {targetEFaktura && openEFaktura && (
                <EFakturaModal
                    account={debitAccount}
                    show={openEFaktura}
                    eFaktura={targetEFaktura}
                    setShow={async (open) => {
                        setOpenEFaktura(open);
                        if (!open) await refetchAndReset();
                    }}
                />
            )}
            <EikaModal
                contentShouldStretch
                open={openApproveAllModal}
                setOpen={setOpenApproveAllModal}
                onClose={refetchAndReset}
            >
                <MultipleApprovalEFakturaModalContent
                    eFakturas={eFakturas}
                    formattedTotalAmount={formattedTotalAmount}
                    debitAccountState={debitAccountState}
                    onClose={() => setOpenApproveAllModal(false)}
                    accounts={accounts}
                    errorState={[error, setError]}
                />
            </EikaModal>
        </section>
    );
};

export default () => (
    <ErrorCatcher
        queriesToInvalidateOnRetry={[EFAKTURA_QUERY_KEY, ACCOUNTS_QUERY_KEY]}
        appNamespace="multiple-approve-efaktura-list"
    >
        <Suspense fallback={<MultipleApprovalEFakturaListSkeleton />}>
            <SigningContextProvider
                analyticsEvent={AnalyticsEvents.BATCH_EFAKTURA}
                analyticsCategory={ANALYTICS_CATEGORIES.BATCH_EFAKTURA}
            >
                <MultipleApprovalEFakturaList />
            </SigningContextProvider>
        </Suspense>
    </ErrorCatcher>
);
